/**
 * NpcShips — neutral MTS_1 miner ships that drift around a planet system and mine
 * rocks just like the player (same beams/impacts), but WITHOUT per-tick floating
 * numbers (kept cheap). Each NPC has HP + a regenerating shield and bounces off
 * rocks it flies into. When an NPC mines a rock out it reports it via `onDeplete`
 * (which drives the explosion) — it awards the player no XP.
 */
import { Vector2 } from "@/math/Vector2";
import { NPC } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";

export interface NpcShip {
  pos: Vector2;
  vel: Vector2;
  heading: number;
  targetId: string | null;
  impact: Vector2 | null; // live mining impact point (drives the beams) or null
  impactOffset: Vector2;
  spotTimer: number;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  bounceVel: Vector2;
  bounceTimer: number;
  wanderPhase: number;
}

export type NpcDeplete = (body: MineableBody) => void;
export type NpcDeath = (x: number, y: number) => void;

export class NpcManager {
  readonly ships: NpcShip[] = [];

  constructor() {
    for (let i = 0; i < NPC.count; i++) this.ships.push(this.makeShip());
  }

  private makeShip(): NpcShip {
    const a = Math.random() * Math.PI * 2;
    const d = 350 + Math.random() * 1000;
    return {
      pos: new Vector2(Math.cos(a) * d, Math.sin(a) * d),
      vel: new Vector2(),
      heading: a,
      targetId: null,
      impact: null,
      impactOffset: new Vector2(),
      spotTimer: 0,
      hp: NPC.maxHp,
      maxHp: NPC.maxHp,
      shield: NPC.maxShield,
      maxShield: NPC.maxShield,
      shieldFlash: 0,
      hitTimer: 999,
      bounceVel: new Vector2(),
      bounceTimer: 0,
      wanderPhase: Math.random() * 100,
    };
  }

  update(
    dt: number,
    bodies: MineableBody[],
    bodyById: (id: string) => MineableBody | null,
    onDeplete: NpcDeplete,
    onDeath: NpcDeath,
  ): void {
    for (const s of this.ships)
      this.updateShip(s, dt, bodies, bodyById, onDeplete, onDeath);
  }

  private updateShip(
    s: NpcShip,
    dt: number,
    bodies: MineableBody[],
    bodyById: (id: string) => MineableBody | null,
    onDeplete: NpcDeplete,
    onDeath: NpcDeath,
  ): void {
    // Destroyed (e.g. by the player's turrets) → explode + respawn at the edge.
    if (s.hp <= 0) {
      onDeath(s.pos.x, s.pos.y);
      this.respawn(s);
      return;
    }
    s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
    s.hitTimer += dt;

    // Knock-back after a collision (also acts as i-frames).
    if (s.bounceTimer > 0) {
      s.bounceTimer -= dt;
      s.pos.x += s.bounceVel.x * dt;
      s.pos.y += s.bounceVel.y * dt;
      s.bounceVel.x *= 0.92;
      s.bounceVel.y *= 0.92;
      s.heading = Math.atan2(s.bounceVel.y, s.bounceVel.x);
      s.impact = null;
      this.regenShield(s, dt);
      return;
    }

    let target = s.targetId ? bodyById(s.targetId) : null;
    if (!target || target.ore <= 0) {
      target = this.pickTarget(s, bodies);
      s.targetId = target ? target.id : null;
      s.impact = null;
    }

    if (!target) {
      this.cruise(s, dt, null);
    } else {
      const toX = target.pos.x - s.pos.x;
      const toY = target.pos.y - s.pos.y;
      const dist = Math.hypot(toX, toY);
      if (dist > NPC.miningRange + target.radius) {
        this.cruise(s, dt, target.pos);
        s.impact = null;
      } else {
        // In range → hold and mine.
        s.vel.set(0, 0);
        s.heading = Math.atan2(toY, toX);
        s.spotTimer -= dt;
        if (!s.impact || s.spotTimer <= 0) {
          const t = Math.random() * Math.PI * 2;
          const rr = Math.sqrt(Math.random()) * target.radius * 0.8;
          s.impactOffset.set(Math.cos(t) * rr, Math.sin(t) * rr);
          s.spotTimer = NPC.attackHold;
          s.impact = s.impact ?? new Vector2();
        }
        s.impact.set(
          target.pos.x + s.impactOffset.x,
          target.pos.y + s.impactOffset.y,
        );
        const got = Math.min(NPC.mineRatePerSec * dt, target.ore);
        target.ore -= got;
        if (target.ore <= 0) {
          onDeplete(target);
          s.targetId = null;
          s.impact = null;
        }
      }
    }

    this.collide(s, bodies, onDeath);
    this.regenShield(s, dt);
  }

  private cruise(s: NpcShip, dt: number, goal: Vector2 | null): void {
    let desired: number;
    if (goal) {
      desired = Math.atan2(goal.y - s.pos.y, goal.x - s.pos.x);
    } else {
      s.wanderPhase += dt;
      desired = s.heading + Math.sin(s.wanderPhase * 0.6) * 0.6;
    }
    let diff = desired - s.heading;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const maxTurn = NPC.turnRate * dt;
    s.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
    const vx = Math.cos(s.heading) * NPC.speed;
    const vy = Math.sin(s.heading) * NPC.speed;
    s.vel.set(vx, vy);
    s.pos.x += vx * dt;
    s.pos.y += vy * dt;
  }

  private collide(s: NpcShip, bodies: MineableBody[], onDeath: NpcDeath): void {
    const cr = NPC.collisionRadius;
    for (const b of bodies) {
      if (b.ore <= 0 || b.id === s.targetId) continue;
      const rr = cr + b.radius;
      const dx = s.pos.x - b.pos.x;
      const dy = s.pos.y - b.pos.y;
      if (dx * dx + dy * dy < rr * rr) {
        let dmg = NPC.collisionDamage;
        if (s.shield > 0) {
          const a = Math.min(s.shield, dmg);
          s.shield -= a;
          dmg -= a;
        }
        if (dmg > 0) s.hp = Math.max(0, s.hp - dmg);
        s.shieldFlash = 1;
        s.hitTimer = 0;
        const len = Math.hypot(dx, dy) || 1;
        const nx = dx / len;
        const ny = dy / len;
        s.pos.set(b.pos.x + nx * (rr + 2), b.pos.y + ny * (rr + 2));
        s.bounceVel.set(nx * NPC.bounceSpeed, ny * NPC.bounceSpeed);
        s.bounceTimer = NPC.bounceTime;
        s.targetId = null;
        s.impact = null;
        if (s.hp <= 0) {
          onDeath(s.pos.x, s.pos.y);
          this.respawn(s);
        }
        break;
      }
    }
  }

  private regenShield(s: NpcShip, dt: number): void {
    if (s.hitTimer >= NPC.regenDelay && s.shield < s.maxShield) {
      s.shield = Math.min(s.maxShield, s.shield + NPC.shieldRegenPerSec * dt);
    }
  }

  private respawn(s: NpcShip): void {
    const a = Math.random() * Math.PI * 2;
    const d = 700 + Math.random() * 500;
    s.pos.set(Math.cos(a) * d, Math.sin(a) * d);
    s.vel.set(0, 0);
    s.hp = s.maxHp;
    s.shield = s.maxShield;
    s.bounceTimer = 0;
    s.targetId = null;
    s.impact = null;
  }

  private pickTarget(s: NpcShip, bodies: MineableBody[]): MineableBody | null {
    const alive = bodies.filter((b) => b.ore > 0);
    if (alive.length === 0) return null;
    alive.sort(
      (a, b) =>
        (a.pos.x - s.pos.x) ** 2 +
        (a.pos.y - s.pos.y) ** 2 -
        ((b.pos.x - s.pos.x) ** 2 + (b.pos.y - s.pos.y) ** 2),
    );
    const pool = alive.slice(0, Math.min(6, alive.length));
    return pool[Math.floor(Math.random() * pool.length)] ?? null;
  }
}
